// packages/core/src/learning/school-modules/keynesian-mmt.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Keynesian / MMT". surface: 'school',
// homeSchool: 'keynesian-mmt'. module id 'school-keynesian-mmt' matches the
// exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #47668f) — flag for the presentation-tuning pass.
// tier/pillar: macro-policy worldview lens (SchoolMeta bucket
// 'macro-wealth-perspectives') → tier 'growth', pillar 'money-psychology-core'
// (per the protocol default: worldview/mindset lenses → growth /
// money-psychology-core). Same mapping as the sibling macro schools
// anti-fragility, hard-money, and inequality-awareness. Flag for the
// presentation-tuning pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_KEYNESIAN_MMT_MODULE: LearningModule = {
	id: 'school-keynesian-mmt',
	title: 'Keynesian / MMT',
	description:
		'The lens that reads public money — demand, jobs, deficits, and currency power — as a tradeoff between capacity, inflation, and results, refusing both the household-budget analogy and the pretense that public spending has no limits.',
	emoji: '🏛️', // PLACEHOLDER — tune in presentation pass
	accentColor: '#47668f', // from SchoolMeta color — tune in presentation pass
	tier: 'growth',
	pillar: 'money-psychology-core',
	order: 0,
	surface: 'school',
	homeSchool: 'keynesian-mmt',
	lessons: [
		{
			id: 'school-keynesian-mmt-01',
			title: 'A country is not a household',
			description:
				'Learn why Keynesian and MMT rejects simple household budget analogies for currency issuing governments.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['keynesian-mmt'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'sovereign-currency',
				'household-analogy',
				'public-budget-lens',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'sovereign currency',
				'household analogy',
				'public budget lens',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A household has to earn, borrow, or spend from savings before it can pay bills. A currency issuing government does not work the same way.',
				},
				{
					type: 'paragraph',
					text: 'Keynesian and MMT believes the household budget analogy hides more than it explains. Governments that issue their own currency face real limits, but those limits are not identical to the limits facing a family, worker, or business.',
				},
				{
					type: 'bullet_list',
					items: [
						'Households use currency',
						'Currency issuers create currency',
						'Real limits still matter',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is saying the government should budget like a family without asking what kind of money system exists.',
				},
				{
					type: 'example',
					text: 'A family cannot create dollars to pay rent. A national government that issues its own currency can create money to make payments, but if too much spending hits limited goods, labor, housing, or energy, inflation can rise. Different constraint, still a constraint.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can be misused. Saying the government is not a household does not mean spending has no cost. The cost may show up as inflation, weak trust, poor allocation, or political waste.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens is not about taking a political side. It is about separating false analogies from real tradeoffs. Ask what the spending changes in real life, what capacity exists, and what risks follow.',
				},
				{
					type: 'key_takeaway',
					text: 'A currency issuing government is not a household, but it still lives inside real limits.',
				},
			],
			audioScript:
				'Keynesian and MMT starts by challenging one of the most common lines in money talk: “The government should budget like a household.” It sounds responsible. It sounds common sense. But this lens says the comparison is incomplete.\n\nA household uses currency. You and I cannot create dollars to pay rent. We have to earn income, use savings, borrow, sell something, or get help. If we spend more than we bring in for too long, we eventually hit a hard wall. Bills are due. Debt gets expensive. Credit runs out. That is household reality.\n\nA national government that issues its own currency is different. It is not just another household with a bigger checking account. It can create the currency it spends. It can make payments in its own money. That changes the nature of the constraint. The question is not simply, “Where will the dollars come from?” The deeper question is, “What happens when those dollars enter the real economy?”\n\nThis is where people often talk past each other. Keynesian and MMT does not mean spending is free. It means the limit is not exactly the same as a family budget. The real limits include inflation, available workers, factories, housing, energy, food, materials, public trust, institutional competence, and political discipline. If the government spends into an economy with unused workers and idle capacity, that spending may help create jobs and income. If it spends into an economy already hitting real limits, it may bid up prices.\n\nThink of it this way. A family cannot create dollars to pay a contractor. A currency issuing government can create dollars to pay for a public project. But if there are not enough skilled workers, materials, land, or management capacity, more money alone does not magically create the bridge, clinic, housing, or energy system. The money is a claim on real resources. If the real resources are scarce, the pressure shows up somewhere.\n\nThe useful part of this lens is that it clears away a lazy analogy. It stops people from saying, “The government is just like a household,” as if that settles the debate. It does not. But the blind spot is swinging too far and acting like public spending has no serious limits. That is also wrong. Inflation, waste, corruption, poor execution, and bad priorities are real risks.\n\nInside Nisk, this lesson should train a better question. Do not ask only whether public spending increases a deficit. Ask what it buys, what problem it solves, whether real capacity exists, and what risk it creates. That is the tradeoff.\n\nA country is not a household. But reality still sends the bill in other ways.',
			flashcards: [
				{
					id: 'school-keynesian-mmt-01-fc-1',
					role: 'main_idea',
					front: 'Why is the household analogy weak?',
					back: 'A currency issuing government has different constraints than a family.',
				},
				{
					id: 'school-keynesian-mmt-01-fc-2',
					role: 'watch_out',
					front: 'What is the misuse?',
					back: 'Acting like different constraints mean no constraints.',
				},
				{
					id: 'school-keynesian-mmt-01-fc-3',
					role: 'use_it',
					front: 'What should you ask instead?',
					back: 'What real resources, inflation risks, and results are involved.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Test the analogy',
				prompt: 'Write one public budget debate, then separate the household analogy from the real economic limits.',
			},
			revision: 1,
		},
		{
			id: 'school-keynesian-mmt-02',
			title: 'Demand can decide jobs',
			description:
				'Learn why Keynesian and MMT sees unemployment as more than individual failure.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['keynesian-mmt'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'demand-shortfall',
				'unemployment-signal',
				'public-stabilization',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'demand shortfall',
				'unemployment signal',
				'public stabilization',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'People can be willing to work and still be stuck in a weak economy. That is the part this lens refuses to ignore.',
				},
				{
					type: 'paragraph',
					text: 'Keynesian and MMT believes demand matters. If households, businesses, and governments are not spending enough to use available workers and capacity, unemployment can persist even when people are capable and motivated.',
				},
				{
					type: 'bullet_list',
					items: [
						'Weak demand reduces hiring',
						'Lost income reduces spending',
						'Public support can stabilize demand',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating unemployment only as a personal effort problem.',
				},
				{
					type: 'example',
					text: 'A restaurant may want to hire when customers are coming in. If demand collapses, the owner cuts hours even if workers are skilled. The worker did not suddenly lose character. The demand environment changed.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that public support has to be designed and timed well. Too little support can deepen pain. Too much spending into a constrained economy can add price pressure.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens helps users understand why macro conditions affect personal options. Your career, income, rent, and runway do not float outside the economy.',
				},
				{
					type: 'key_takeaway',
					text: 'A weak economy can waste capable people, and that waste becomes a money problem for households.',
				},
			],
			audioScript:
				'Keynesian and MMT pays close attention to unemployment because it does not treat every lost job as a personal failure. That is important. Of course individual choices matter. Skills, reliability, effort, and judgment all matter. But sometimes people are ready to work and the economy is not ready to use them.\n\nThis lens says demand matters. Demand means people, businesses, and governments are buying enough goods and services to keep the economy active. When demand is strong, businesses see customers, orders, projects, and revenue. They are more likely to hire, raise hours, invest, and take chances. When demand collapses, businesses cut back. They may lay people off, pause hiring, reduce hours, or cancel plans.\n\nThat is why a worker can be skilled and still struggle. Imagine a restaurant during a sharp downturn. The cooks did not forget how to cook. The servers did not forget how to serve. The owner did not suddenly hate business. Customers stopped coming. Revenue fell. Hours got cut. The problem is not only individual effort. It is a demand problem.\n\nKeynesian and MMT sees this as a system level issue. If everyone cuts spending at the same time because they are scared, one person’s spending cut becomes another person’s lost income. That lost income creates more spending cuts. The cycle can feed on itself. Public support can interrupt that cycle by replacing some lost demand, supporting incomes, funding jobs, or stabilizing essential services.\n\nThe useful part of this lens is compassion plus realism. It helps people stop turning macro pain into personal shame. A recession can damage normal households that did nothing wrong. Job markets can weaken. Wages can stall. Businesses can close. Rent can still be due. When that happens, personal finance advice needs to understand the environment, not just lecture the person.\n\nBut the lens has limits. Public support is not automatically good just because it spends money. Design matters. Timing matters. Targeting matters. Capacity matters. If support is too small, it may fail to prevent pain. If it is too broad or continues when supply is constrained, it may add inflation pressure. If programs are poorly run, money can be wasted or captured by the wrong actors.\n\nInside Nisk, this lesson should help users connect macro conditions to their own numbers. Your Dashboard is personal, but the economy around you shapes income, job security, costs, and options. Your runway matters more when hiring weakens. Your skills matter, but the job market also matters. Your plan should not assume perfect economic weather.\n\nDemand can decide jobs. That does not erase agency. It makes agency more honest.',
			flashcards: [
				{
					id: 'school-keynesian-mmt-02-fc-1',
					role: 'main_idea',
					front: 'Why does demand matter?',
					back: 'Weak demand can reduce hiring, income, and hours even for capable workers.',
				},
				{
					id: 'school-keynesian-mmt-02-fc-2',
					role: 'watch_out',
					front: 'What is the judgment trap?',
					back: 'Treating unemployment only as a personal character problem.',
				},
				{
					id: 'school-keynesian-mmt-02-fc-3',
					role: 'use_it',
					front: 'How do you apply this?',
					back: 'Connect career and runway planning to the wider economy.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your job shock runway',
			},
			revision: 1,
		},
		{
			id: 'school-keynesian-mmt-03',
			title: 'Deficits are not one thing',
			description:
				'Learn why Keynesian and MMT asks what a deficit does before judging it.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['keynesian-mmt'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: [
				'deficit-purpose',
				'sector-balances',
				'public-spending-results',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'deficit purpose',
				'sector balances',
				'public spending results',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'The word deficit can make people stop thinking. They hear the number and assume the answer is already obvious.',
				},
				{
					type: 'paragraph',
					text: 'Keynesian and MMT believes deficits should be judged by context and result. A deficit can support private income during a downturn, fund useful public capacity, or waste resources badly. The label alone does not tell you which one happened.',
				},
				{
					type: 'bullet_list',
					items: [
						'A deficit can support income',
						'A deficit can fund real capacity',
						'A deficit can also waste resources',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating all deficits as identical.',
				},
				{
					type: 'example',
					text: 'Borrowing to fund a useless project is different from spending that keeps workers employed during a downturn or builds infrastructure that improves real capacity. Both may show up as deficits, but the economic meaning differs.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is using context as a way to avoid discipline. If every deficit is defended without checking inflation, execution, and results, the lens becomes sloppy.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a judgment habit. Ask what the deficit is for, who receives income, what real capacity changes, and what risk is created.',
				},
				{
					type: 'key_takeaway',
					text: 'The important question is not only how large the deficit is. It is what the deficit does.',
				},
			],
			audioScript:
				'Deficits create loud arguments because the word itself carries emotion. Some people hear deficit and think irresponsibility. Other people hear deficit and think public investment. Keynesian and MMT tries to slow the conversation down. It asks: what is the deficit doing?\n\nThat question matters because not all deficits are the same. A deficit during a deep downturn can support income when the private sector is pulling back. If households and businesses are cutting spending, public spending can keep money flowing, jobs attached, and services alive. In that case, the deficit may be part of stabilization.\n\nA deficit can also fund public capacity. Roads, transit, energy systems, research, schools, health infrastructure, and disaster preparation can affect what the economy can produce later. If spending creates real capacity, the story is different from spending that disappears into waste.\n\nBut deficits can also be badly used. Money can fund projects that do not solve real problems. It can be captured by insiders. It can support demand when supply is already constrained. It can add inflation pressure. It can become political habit rather than economic need. Keynesian and MMT is strongest when it admits that.\n\nOne useful idea connected to this lens is sector balances. In simple terms, one sector’s deficit can be another sector’s surplus. If the government spends more into the economy than it taxes out, that can become income or saving for the private sector, depending on the full picture including trade. This does not make every deficit good. It just means the government balance is connected to private financial positions. You cannot judge one side without asking what is happening to the others.\n\nHere is a practical example. Suppose public spending keeps workers employed during a severe downturn. Those workers pay rent, buy food, avoid default, and keep businesses alive. That deficit has one meaning. Now imagine spending on a poorly designed project that absorbs money, raises costs, and creates little useful output. That deficit has a different meaning. The word is the same. The result is not.\n\nThe common mistake is treating all deficits as either evil or harmless. Both views are lazy. The better question is: what problem is being solved, what real resources are being used, who benefits, what capacity changes, and what risks follow?\n\nInside Nisk, this matters because macro debates affect personal lives. Deficits can show up as jobs, benefits, infrastructure, taxes, inflation, interest rates, and public services. You do not need to become a macro economist to ask better questions. You need to move past slogans.\n\nA deficit is not one thing. It is a tool, a signal, a transfer, and a tradeoff. Judge what it does.',
			flashcards: [
				{
					id: 'school-keynesian-mmt-03-fc-1',
					role: 'main_idea',
					front: 'Are all deficits the same?',
					back: 'No. Their meaning depends on context, purpose, results, and risk.',
				},
				{
					id: 'school-keynesian-mmt-03-fc-2',
					role: 'watch_out',
					front: 'What is the deficit trap?',
					back: 'Calling every deficit either disaster or harmless.',
				},
				{
					id: 'school-keynesian-mmt-03-fc-3',
					role: 'use_it',
					front: 'What should you ask?',
					back: 'What does the deficit fund, who benefits, and what risk follows?',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Judge the deficit',
				prompt: 'Write one deficit funded idea, then list its intended result, real resource need, and possible risk.',
			},
			revision: 1,
		},
		{
			id: 'school-keynesian-mmt-04',
			title: 'Inflation is the real constraint',
			description:
				'Learn why Keynesian and MMT treats inflation and real capacity as harder limits than the deficit number alone.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['keynesian-mmt'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['inflation-constraint', 'real-resources', 'capacity-limit'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'inflation constraint',
				'real resources',
				'capacity limit',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Keynesian and MMT does not say spending has no limit. The serious version says the limit is real capacity and inflation.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes money can be created more easily than housing, nurses, energy, food, materials, or skilled labor. If spending rises faster than real capacity, prices can rise instead of output.',
				},
				{
					type: 'bullet_list',
					items: [
						'Money is not the same as capacity',
						'Inflation signals pressure',
						'Real resources set limits',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is acting like money can solve a shortage by itself.',
				},
				{
					type: 'example',
					text: 'A government can fund housing demand, but if there are not enough builders, land, permits, materials, or time, more money can push prices higher instead of creating enough homes.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that inflation control can also hurt people if done carelessly. Crushing demand may reduce price pressure, but it can also cost jobs and income.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should make users think in real constraints. Ask what prices, wages, rent, and public choices do to household runway.',
				},
				{
					type: 'key_takeaway',
					text: 'Public money can move demand, but real resources decide whether that demand becomes output or inflation.',
				},
			],
			audioScript:
				'The serious version of Keynesian and MMT has to say this clearly: spending has limits. The limit is not always the deficit number by itself. The limit is what the real economy can absorb without creating damaging inflation.\n\nThis is where the lens becomes more disciplined. A government that issues its own currency can create money. But it cannot instantly create nurses, houses, energy, food, ports, teachers, electricians, factories, childcare, or trust. Money is a claim on resources. If the resources exist and are underused, spending may bring them into action. If the resources are already stretched, more spending can push prices up.\n\nHousing is a good example. A government can give people more money for housing or support demand for home buying or rent. But if there are not enough homes, land use is constrained, construction is slow, labor is limited, and materials are expensive, more money can chase the same limited supply. The result may be higher prices instead of enough new housing. Money did not create the missing capacity fast enough.\n\nThis is why inflation matters so much in Keynesian and MMT debates. Critics often say this lens ignores inflation. A sloppy version might. A serious version does not. It says inflation is the real warning light. If spending is pushing demand beyond capacity, the economy is telling you something. The answer may involve taxes, reduced spending, supply building, better targeting, interest policy, regulation, or time. It depends on the cause.\n\nInflation is also not just one thing. Prices can rise because demand is too strong. They can rise because supply is broken. They can rise because energy shocks hit. They can rise because housing is scarce. They can rise because firms have pricing power. They can rise because wages, imports, policy, or expectations shift. A serious lens asks which inflation problem it is before claiming one fix.\n\nThe tradeoff is painful. Fighting inflation by crushing demand can reduce price pressure, but it can also cost people jobs, hours, income, and security. Ignoring inflation can also hurt people, especially workers and households whose wages do not keep up. This is why macro policy is hard. There are no costless buttons.\n\nInside Nisk, this connects directly to real life. Inflation changes your Dashboard. It changes expenses, runway, savings power, rent pressure, and the freedom path. Public money debates may sound distant, but the result can show up in the price of groceries, job security, public services, and wages.\n\nThe takeaway is not “spend without limit.” It is also not “never run deficits.” The better takeaway is: public money can move demand, but real resources decide whether that demand becomes useful output or higher prices.',
			flashcards: [
				{
					id: 'school-keynesian-mmt-04-fc-1',
					role: 'main_idea',
					front: 'What is the real constraint?',
					back: 'Inflation and real capacity, not only the deficit number.',
				},
				{
					id: 'school-keynesian-mmt-04-fc-2',
					role: 'watch_out',
					front: 'What can money not create instantly?',
					back: 'Housing, labor, energy, food, materials, skill, and trust.',
				},
				{
					id: 'school-keynesian-mmt-04-fc-3',
					role: 'use_it',
					front: 'What should you watch?',
					back: 'Whether spending creates real output or mostly higher prices.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate higher costs',
				scenarioType: 'custom',
			},
			revision: 1,
		},
		{
			id: 'school-keynesian-mmt-05',
			title: 'Public power can fail too',
			description:
				'Learn where Keynesian and MMT breaks down when confidence in public spending ignores politics and execution.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['keynesian-mmt'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: [
				'public-execution-risk',
				'political-constraint',
				'policy-humility',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'public execution risk',
				'political constraint',
				'policy humility',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Public money can solve real problems. It can also be wasted, mistimed, captured, or aimed at the wrong thing.',
				},
				{
					type: 'paragraph',
					text: 'Keynesian and MMT is strongest when it admits that government capacity is not magic. Spending needs competence, trust, real resources, good timing, and political discipline.',
				},
				{
					type: 'bullet_list',
					items: [
						'Good goals can be badly executed',
						'Politics can distort spending',
						'Trust can weaken if promises outrun results',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating public intention as the same thing as public outcome.',
				},
				{
					type: 'example',
					text: 'Funding a transit project can improve a region if it is well planned and built. If it becomes delayed, over budget, poorly located, or politically captured, the money spent does not automatically become public value.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is assuming private markets fail but public systems simply fix. Both can fail in different ways. The real question is which institution can solve this problem with the least damage and the best result.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens ends with humility. Ask what public money is trying to achieve, whether the capacity exists, and how failure would show up in normal households.',
				},
				{
					type: 'key_takeaway',
					text: 'Public money is powerful, but power still needs competence, limits, and accountability.',
				},
			],
			audioScript:
				'Keynesian and MMT can be very useful because it reminds people that public money has power. Governments can stabilize demand, support jobs, build infrastructure, fund services, and respond to crises. Those are real tools. But this lens breaks down if it acts like public spending automatically becomes public value.\n\nGood intentions are not enough. A program can have a worthy goal and still fail through poor design, weak management, political capture, corruption, bad timing, or lack of real capacity. Money can be appropriated, announced, and celebrated, but the road, clinic, school, housing, or service still has to be built and operated well. Execution matters.\n\nThis is where a mature Keynesian and MMT lens needs humility. It should not only criticize markets. Markets can fail, yes. They can underprovide public goods, ignore people without buying power, create instability, and leave communities behind. But public systems can fail too. They can be slow, captured, wasteful, short sighted, or too political. Neither side gets a free pass.\n\nTake a transit project. In theory, public spending on transit can reduce commute pressure, connect workers to jobs, reduce household transportation costs, and improve regional productivity. That could be a strong use of public money. But if the project is badly planned, delayed for years, placed where it does not serve real demand, or eaten by inflated costs, the outcome changes. The spending happened, but the value may not arrive.\n\nThis matters because trust is part of money. If public promises keep outrunning public results, people lose faith. If citizens see spending but not competence, they may become more hostile to future projects, even good ones. That creates a political cost. A society needs both capacity and trust to use public money well.\n\nThe blind spot is confusing the ability to spend with the ability to solve. A currency issuing government may be able to fund something, but funding is only one step. Are the workers available? Are materials available? Is the project well designed? Is there accountability? What happens if costs rise? Who benefits? Who pays through inflation, taxes, or reduced trust if it fails?\n\nInside Nisk, this lens should end with balanced judgment. Public money can help. Deficit fear can be overdone. Unemployment is not just personal failure. But inflation, capacity, politics, and execution are real. A worldview that ignores those limits becomes sloppy.\n\nThe practical question is simple: what is the public money supposed to accomplish, and what would prove that it actually worked? That question keeps the lens grounded.',
			flashcards: [
				{
					id: 'school-keynesian-mmt-05-fc-1',
					role: 'main_idea',
					front: 'Can public power fail?',
					back: 'Yes. Spending needs competence, timing, capacity, trust, and accountability.',
				},
				{
					id: 'school-keynesian-mmt-05-fc-2',
					role: 'watch_out',
					front: 'What is the intention trap?',
					back: 'Treating a good public goal as proof of a good public outcome.',
				},
				{
					id: 'school-keynesian-mmt-05-fc-3',
					role: 'use_it',
					front: 'What should you ask?',
					back: 'What result should public money create, and how would failure show up?',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Test the public outcome',
				prompt: 'Write one public spending goal, then define what success, failure, and household impact would look like.',
			},
			revision: 1,
		},
	],
};
